import { FC, useCallback, useEffect, useRef } from "react";
import { useUser } from "@clerk/react";
import { Moon, Sun } from "lucide-react";
import { Button } from "@heroui/react";

interface ThemeSwitchProps {
  className?: string;
  clerkThemeKey?: string;
  "aria-label"?: string;
}

export const THEME_STORAGE_KEY = "theme";

const CLERK_SYNC_DEBOUNCE_MS = 600;

export const ThemeSwitch: FC<ThemeSwitchProps> = ({
  className,
  clerkThemeKey = THEME_STORAGE_KEY,
  "aria-label": ariaLabelProp,
}) => {
  const { user, isSignedIn } = useUser();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const syncToClerk = useCallback(async () => {
    if (!isSignedIn || !user) return;

    const current = document.documentElement.classList.contains("dark") ? "dark" : "light";
    try {
      await user.updateMetadata({ unsafeMetadata: { [clerkThemeKey]: current } });
    } catch (err) {
      console.error("Failed to save theme to Clerk:", err);
    }
  }, [isSignedIn, user, clerkThemeKey]);

  const toggle = () => {
    const next = !document.documentElement.classList.contains("dark");
    const nextTheme = next ? "dark" : "light";

    document.documentElement.classList.toggle("dark", next);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, nextTheme);
    } catch (err) {
      console.error("Failed to save theme to localStorage:", err);
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void syncToClerk();
    }, CLERK_SYNC_DEBOUNCE_MS);
  };

  return (
    <Button
      type="button"
      onPress={toggle}
  
      isIconOnly
      aria-label={ariaLabelProp ?? "Toggle color theme"}
      className={`icon-button md:shadow-none! md:bg-surface! md:border-none! size-9! min-w-9 min-h-9 ${className ?? ""}`}
    >
      <Moon size={22} className="dark:hidden lg:text-[#3c3e6f] text-default-500" />
      <Sun size={22} className="hidden dark:block lg:text-[#7d7db3] text-default-500" />
    </Button>
  );
};